import { offlineDb, type MyContactsCache, type OfflineDb } from '@/lib/offline/offlineDb'
import type { MyContact, RegisteredContact } from './registrationService'

/** Keeps the collector's synced contacts for an event, so All Mine still lists them offline. */
export async function saveMyContacts(userId: string, eventId: string, contacts: RegisteredContact[], db: OfflineDb = offlineDb, now = new Date()) {
  await db.myContacts.put({ userId, eventId, contacts, cachedAt: now.toISOString() })
}

export async function readMyContacts(userId: string, eventId: string, db: OfflineDb = offlineDb): Promise<MyContactsCache | undefined> {
  const cached = await db.myContacts.get([userId, eventId])
  if (!cached) return undefined

  // IndexedDB retains records from prior app versions. Confirmation status was
  // introduced after this cache existed, so old copies must stay usable while
  // offline and take the database default until a later online refresh.
  return {
    ...cached,
    contacts: cached.contacts.map((contact) => ({
      ...contact,
      confirmationStatus: contact.confirmationStatus ?? 'maybe',
    })),
  }
}

/** Contacts are personal data: they leave the device with the person who signed out. */
export async function clearMyContacts(userId: string, db: OfflineDb = offlineDb) {
  await db.myContacts.where('userId').equals(userId).delete()
}

/**
 * My contacts (every event) is kept in the same table under this key, so it is
 * cleared with the rest of the person's contacts when they sign out, and needs
 * no new IndexedDB store.
 */
export const allEventsKey = '*'

export async function saveAllMyContacts(userId: string, contacts: MyContact[], db: OfflineDb = offlineDb, now = new Date()) {
  await db.myContacts.put({ userId, eventId: allEventsKey, contacts, cachedAt: now.toISOString() })
}

export async function readAllMyContacts(userId: string, db: OfflineDb = offlineDb): Promise<{ contacts: MyContact[]; cachedAt: string } | undefined> {
  const cached = await db.myContacts.get([userId, allEventsKey])
  if (!cached) return undefined
  return { cachedAt: cached.cachedAt, contacts: (cached.contacts as MyContact[]).map((contact) => ({ ...contact, confirmationStatus: contact.confirmationStatus ?? 'maybe' })) }
}
